import React, { useState, useEffect } from 'react';
import { updateAdminConfig } from '../../utils/api';

/*
  WebsiteMode — its own admin tab.

  Controls the PUBLIC site:
    - Live        → users can connect / register / trade as normal.
    - Development → public site closed; visitors see the Coming Soon page built
                    from the heading / subheading below (admin's own words —
                    there is NO built-in "under development" text).

  Countdown: set a launch date & time → a live countdown shows on the page.
  Leave it off (or clear the date) → no timer, just heading + subheading.
  The admin panel itself is always reachable via ?key=.
*/

const toLocalInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  const off = dt.getTimezoneOffset() * 60000;
  return new Date(dt - off).toISOString().slice(0, 16);
};

const WebsiteMode = ({ config, loadConfig, flash }) => {
  const isDev = config.siteMode === 'development';

  const [heading, setHeading] = useState(config.comingSoonHeading || '');
  const [subheading, setSubheading] = useState(config.comingSoonSubheading || '');
  const [timerEnabled, setTimerEnabled] = useState(!!config.launchAt);
  const [launchAt, setLaunchAt] = useState(toLocalInput(config.launchAt));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHeading(config.comingSoonHeading || '');
    setSubheading(config.comingSoonSubheading || '');
    setTimerEnabled(!!config.launchAt);
    setLaunchAt(toLocalInput(config.launchAt));
  }, [config]);

  const toggleSiteMode = async () => {
    const next = isDev ? 'live' : 'development';
    try {
      await updateAdminConfig({ siteMode: next });
      flash(next === 'live' ? 'Website is now LIVE' : 'Website set to DEVELOPMENT (closed to users)');
      loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateAdminConfig({
        comingSoonHeading: heading.trim(),
        comingSoonSubheading: subheading.trim(),
        launchAt: (timerEnabled && launchAt) ? new Date(launchAt).toISOString() : null,
      });
      flash('Coming Soon page saved'); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setSaving(false);
  };

  return (
    <div className="fade-in wm-root">
      <style>{`
        .wm-root { width: 100%; box-sizing: border-box; }
        .wm-root * { box-sizing: border-box; }
        .wm-sub { font-weight: 400; opacity: .6; font-size: 12px; text-transform: none; letter-spacing: 0; }
        .wm-section-title { margin: 26px 0 12px; font-size: 12.5px; font-weight: 700; text-transform: uppercase;
          letter-spacing: .07em; display: flex; align-items: center; gap: 10px; opacity: .95; }
        .wm-section-title::before { content: ''; width: 4px; height: 15px; border-radius: 3px; background: var(--accent); flex-shrink: 0; }
        .wm-card { padding: 20px 22px; border-radius: 14px; }
        .wm-status-card { border-left: 4px solid #16a34a; }
        .wm-status-card.dev { border-left-color: #f59e0b; }
        .wm-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        .wm-status { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
        .wm-dot { width: 12px; height: 12px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; }
        .wm-dot.live { background: #16a34a; box-shadow: 0 0 0 4px rgba(22,163,74,.18); }
        .wm-dot.dev { background: #f59e0b; box-shadow: 0 0 0 4px rgba(245,158,11,.18); }
        .wm-mode { font-weight: 700; font-size: 15px; }
        .wm-desc { font-size: 12.5px; opacity: .68; margin-top: 3px; line-height: 1.5; max-width: 480px; }
        .wm-switch { display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 12px 16px;
          background: var(--bg-sidebar); border: 1px solid var(--border); border-radius: 14px; min-width: 168px; flex-shrink: 0; }
        .wm-switch-info { display: flex; flex-direction: column; gap: 4px; }
        .wm-switch-label { font-weight: 700; font-size: 14px; }
        .wm-state { font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
        .wm-state.on { color: #16a34a; } .wm-state.off { color: #ef4444; }
        .wm-toggle { position: relative; flex-shrink: 0; width: 46px; height: 26px; border-radius: 999px;
          background: rgba(128,128,128,.35); transition: background .2s ease; margin-left: auto; }
        .wm-toggle.on { background: #16a34a; }
        .wm-knob { position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 50%;
          background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.35); transition: transform .2s ease; }
        .wm-toggle.on .wm-knob { transform: translateX(20px); }
        .wm-field { margin-top: 16px; }
        .wm-field label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
        .wm-field .input, .wm-field textarea.input { width: 100%; }
        .wm-hint { font-size: 11.5px; opacity: .65; margin-top: 5px; line-height: 1.45; }
        .wm-check { display: flex; align-items: center; gap: 9px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
        .wm-check input { width: 16px; height: 16px; }
        .wm-mt { margin-top: 18px; }
      `}</style>

      <h2 className="adm-title">Website Mode</h2>

      {/* Status + toggle */}
      <h3 className="wm-section-title">Site Status <span className="wm-sub">— open the site, or close it with your Coming Soon page</span></h3>
      <div className={`card wm-card wm-status-card ${isDev ? 'dev' : 'live'}`}>
        <div className="wm-head">
          <div className="wm-status">
            <span className={`wm-dot ${isDev ? 'dev' : 'live'}`} />
            <div>
              <div className="wm-mode">{isDev ? 'Development — site closed' : 'Live — site open to users'}</div>
              <div className="wm-desc">
                {isDev
                  ? 'Visitors see your Coming Soon page. Wallet connect & registration are blocked. (This admin panel still works.)'
                  : 'Users can connect their wallet, register, buy and sell as normal.'}
              </div>
            </div>
          </div>
          <button type="button" role="switch" aria-checked={!isDev} className="wm-switch" onClick={toggleSiteMode}>
            <span className="wm-switch-info">
              <span className="wm-switch-label">{isDev ? 'Development' : 'Live'}</span>
              <span className={`wm-state ${isDev ? 'off' : 'on'}`}>{isDev ? 'Closed' : 'Open'}</span>
            </span>
            <span className={`wm-toggle ${isDev ? '' : 'on'}`}><span className="wm-knob" /></span>
          </button>
        </div>
      </div>

      {/* Coming Soon content */}
      <h3 className="wm-section-title">Coming Soon Page <span className="wm-sub">— your own words, shown when the site is in Development</span></h3>
      <div className="card wm-card">
        <div className="wm-field">
          <label>Heading</label>
          <input type="text" value={heading} onChange={e => setHeading(e.target.value)} className="input" placeholder="e.g. We’re launching soon" />
          <p className="wm-hint">The big title on the page. Leave empty to just show the token name.</p>
        </div>

        <div className="wm-field">
          <label>Subheading / message</label>
          <textarea value={subheading} onChange={e => setSubheading(e.target.value)} className="input" rows={3} placeholder="e.g. Our platform goes live shortly. Stay tuned!" />
          <p className="wm-hint">Supporting line under the heading. Line breaks are kept.</p>
        </div>

        <div className="wm-field">
          <label className="wm-check">
            <input type="checkbox" checked={timerEnabled} onChange={e => setTimerEnabled(e.target.checked)} />
            Show a countdown timer
          </label>
          {timerEnabled && (
            <div style={{ marginTop: 12 }}>
              <label>Launch date &amp; time</label>
              <input type="datetime-local" value={launchAt} onChange={e => setLaunchAt(e.target.value)} className="input" />
              <p className="wm-hint">A live countdown to this time appears on the page. When it hits zero, visitors are asked to refresh.</p>
            </div>
          )}
        </div>

        <button className="btn btn-primary wm-mt" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save Coming Soon Page'}
        </button>
      </div>
    </div>
  );
};

export default WebsiteMode;
